import { Box, styled, Typography } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { getClientOS } from 'utils/getClientOS';

const ShortcutChip = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.primary[200],
    padding: '0.25rem 0.5rem',
    borderRadius: '1.25rem',
    marginRight: '0.25rem',
    minWidth: '3.5rem'
}));

interface PanelTitleWithShortCutProps {
    title?: string;
    showShortCuts?: boolean;
    shortCutAction?: () => void;
    id?: string;
    useBreakcrumb?: string[];
    onClickHeader?: () => void;
    rightHandPanelWidth?: number;
}

const PanelTitleWithShortCut: React.FC<PanelTitleWithShortCutProps> = ({
    title,
    showShortCuts,
    shortCutAction,
    id,
    useBreakcrumb,
    onClickHeader,
    rightHandPanelWidth
}) => {
    const resizeField = rightHandPanelWidth && rightHandPanelWidth <= 400;

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const isMac = navigator.platform.toUpperCase().includes('MAC');
            const key = event?.key?.toLowerCase();

            if ((isMac && event.metaKey && key === 'e') || (!isMac && event.ctrlKey && key === 'e')) {
                event.preventDefault();
                shortCutAction && shortCutAction();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [shortCutAction]);

    const keys = useMemo(() => {
        const os = getClientOS();
        return os === 'MAC' ? { symbol: '⌘', rest: 'E' } : { symbol: 'Ctrl', rest: 'E' };
    }, []);

    return (
        <Box
            id={id}
            sx={(theme) => ({
                paddingLeft: '0.8745rem',
                pt: '1rem',
                pb: '0.5rem',
                color: theme.palette.secondary.main,
                backgroundColor: theme.palette.background.paper,
                position: 'relative'
            })}
        >
            {useBreakcrumb && (
                <Box display="flex" alignItems="center">
                    <Typography
                        gutterBottom
                        onClick={onClickHeader}
                        sx={(theme) => ({
                            ...theme.typography.subtitle2,
                            color: theme.palette.primary[400],
                            fontSize: '1rem',
                            display: 'inline',
                            alignItems: 'center',
                            cursor: 'pointer',
                            transition: 'text-decoration 0.2s',
                            m: 0,
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        })}
                    >
                        {useBreakcrumb[0]}
                    </Typography>
                    <Typography sx={{ ml: '0.5rem', fontSize: '1rem', color: (theme) => theme.palette.text.secondary }} component="span">
                        {'>'}
                    </Typography>
                    <Typography
                        sx={(theme) => ({
                            ...theme.typography.subtitle3,
                            ml: '0.5rem',
                            fontSize: '1rem',
                            cursor: 'default',
                            textDecoration: 'none'
                        })}
                        component="span"
                    >
                        {useBreakcrumb[1]}
                    </Typography>
                </Box>
            )}
            <Box
                onDoubleClick={shortCutAction}
                display="flex"
                flexDirection={resizeField ? 'column' : 'row'}
                alignItems={resizeField ? 'flex-start' : 'center'}
                justifyContent={resizeField ? 'center' : 'flex-start'}
                gap={1}
                sx={{
                    flexWrap: resizeField ? 'nowrap' : 'nowrap'
                }}
            >
                {!useBreakcrumb && (
                    <Typography
                        sx={(theme) => ({
                            color: theme.palette.secondary.main,
                            fontWeight: 700,
                            fontSize: '1rem',
                            letterSpacing: '0.0094rem',
                            lineHeight: '1.5rem',
                            whiteSpace: resizeField ? 'normal' : 'nowrap'
                        })}
                    >
                        {title}
                    </Typography>
                )}

                {showShortCuts && (
                    <>
                        {!resizeField ||
                            (!useBreakcrumb && (
                                <Box
                                    sx={(theme) => ({
                                        width: '0.375rem',
                                        height: '0.375rem',
                                        bgcolor: theme.palette.primary[500],
                                        borderRadius: '50%',
                                        flexShrink: 0
                                    })}
                                />
                            ))}
                        <Box
                            display="flex"
                            alignItems="center"
                            sx={{
                                gap: resizeField ? '0.25rem' : '0.5rem'
                            }}
                        >
                            <ShortcutChip>
                                <Typography component="span">
                                    <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 500, color: '#777' }}>
                                        {keys.symbol}
                                    </Typography>
                                    {' + '}
                                    <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#777' }}>
                                        {keys.rest}
                                    </Typography>
                                </Typography>
                            </ShortcutChip>
                            <Typography
                                sx={(theme) => ({
                                    color: theme.palette.primary[500],
                                    fontSize: '0.75rem'
                                })}
                            >
                                or Double–click to edit.
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default PanelTitleWithShortCut;
